import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly measurementId = environment.analytics?.measurementId;
  private readonly analyticsEnabled = !!environment.analytics?.enabled && !!this.measurementId;

  private gtagConfigured = false;
  private gtagLoadingPromise: Promise<void> | null = null;

  trackPageView(pagePath: string, pageTitle: string): void {
    const pageLocation = typeof window === 'undefined'
      ? pagePath
      : `${window.location.origin}${pagePath}`;

    this.trackEvent('page_view', {
      page_path: pagePath,
      page_title: pageTitle,
      page_location: pageLocation
    });
  }

  trackEvent(eventName: string, params: Record<string, unknown>): void {
    if (!this.analyticsEnabled || typeof window === 'undefined') {
      return;
    }

    const send = () => {
      const gtagFn = window.gtag;
      if (typeof gtagFn === 'function') {
        gtagFn('event', eventName, params);
      }
    };

    if (typeof window.gtag === 'function' && this.gtagConfigured) {
      send();
      return;
    }

    void this.ensureAnalyticsLoaded().then(send);
  }

  private ensureAnalyticsLoaded(): Promise<void> {
    if (!this.analyticsEnabled || typeof document === 'undefined') {
      return Promise.resolve();
    }

    if (this.gtagLoadingPromise) {
      return this.gtagLoadingPromise;
    }

    this.initializeGtagStub();

    this.gtagLoadingPromise = new Promise(resolve => {
      const currentScript = document.querySelector<HTMLScriptElement>(`script[data-gtag-id="${this.measurementId}"]`);
      if (currentScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(this.measurementId ?? '')}`;
      script.dataset['gtagId'] = this.measurementId ?? '';

      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => resolve(), { once: true });

      document.head.appendChild(script);
    });

    return this.gtagLoadingPromise;
  }

  private initializeGtagStub(): void {
    if (!this.measurementId) {
      return;
    }

    window.dataLayer = window.dataLayer ?? [];
    if (typeof window.gtag !== 'function') {
      window.gtag = (...args: unknown[]) => {
        window.dataLayer?.push(args);
      };
    }

    if (this.gtagConfigured) {
      return;
    }

    window.gtag('js', new Date());
    window.gtag('config', this.measurementId, { send_page_view: false });
    this.gtagConfigured = true;
  }
}
